import { useEffect, useMemo, useState } from "react";
import { LayoutDashboard, Target, TrendingUp, Database, ArrowRight, CircleDot } from "lucide-react";
import api from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const formatRelative = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
};
const STATUS_DOT = { green: "#16a34a", amber: "#f59e0b", red: "#dc2626" };
const STATUS_LABEL = { green: "Activo", amber: "Atención", red: "Crítico" };

export default function ResumenTab({ data, onJump }) {
  const [salesCount, setSalesCount] = useState(null);
  const [archive, setArchive] = useState(null);

  // Lightweight side queries to round out the Resumen KPIs without changing
  // the parent payload contract.
  useEffect(() => {
    api.get("/predictive/summary?year=Todos&month=Todos&brand=Todas&model=Todos&branch=Todas")
       .then((r) => setSalesCount(r.data?.sales_total ?? 0))
       .catch(() => setSalesCount(0));
    api.get("/admin/archive/stats")
       .then((r) => setArchive(r.data))
       .catch(() => setArchive(null));
  }, []);

  const overview = useMemo(() => {
    if (!data) return [];
    const pb = data.powerbi_csv || {};
    const zap = data.zapier || {};
    const archiveTotal = (archive?.digital?.count || 0) + (archive?.raw?.count || 0);
    return [
      {
        key: "crm",
        target: "crm",
        icon: <LayoutDashboard size={20}/>,
        accent: "#111827",
        title: "CRM Overview",
        kpiLabel: "Leads cargados",
        kpiValue: fmt(pb.leads_count),
        last: pb.last_ingest_at ? formatRelative(pb.last_ingest_at) : "—",
        status: (pb.leads_count || 0) > 0 ? "green" : "amber",
        statusMsg: pb.configured ? "PowerBI CSV configurado" : "Falta configurar",
      },
      {
        key: "leads",
        target: "leads",
        icon: <Target size={20}/>,
        accent: "#f4c400",
        title: "Lead Performance",
        kpiLabel: "Leads recibidos",
        kpiValue: fmt(zap.leads_received),
        last: zap.last_lead_at ? formatRelative(zap.last_lead_at) : "—",
        status: zap.last_lead_at ? "green" : "amber",
        statusMsg: zap.last_lead_at ? "Webhook activo" : "Sin actividad reciente",
      },
      {
        key: "predictive",
        target: "predictivo",
        icon: <TrendingUp size={20}/>,
        accent: "#3b82f6",
        title: "Forecast",
        kpiLabel: "Ventas facturadas",
        kpiValue: salesCount === null ? "…" : fmt(salesCount),
        last: salesCount === null ? "" : salesCount > 0 ? "Datos cargados" : "Sube Facturación",
        status: salesCount === null ? "amber" : salesCount > 0 ? "green" : "amber",
        statusMsg: salesCount === null ? "Cargando" : salesCount > 0 ? "Módulo activo" : "Requiere XLSX",
      },
      {
        key: "sistema",
        target: "sistema",
        icon: <Database size={20}/>,
        accent: "#16a34a",
        title: "Sistema · Backup",
        kpiLabel: "Snapshots archivados",
        kpiValue: archive === null ? "…" : fmt(archiveTotal),
        last: "Append-only · auto-snapshot",
        status: archive === null ? "amber" : archiveTotal > 0 ? "green" : "amber",
        statusMsg: archive === null ? "Cargando" : archiveTotal > 0 ? "Protegido" : "Sin snapshots",
      },
    ];
  }, [data, salesCount, archive]);

  return (
    <div className="space-y-6" data-testid="tab-resumen-content">
      <header className="surface p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="kpi-icon-clean" style={{ color: "#f4c400", boxShadow: "inset 0 0 0 1px #f4c4001f" }}>
            <CircleDot size={18}/>
          </div>
          <h2 className="font-display font-extrabold text-xl">Resumen general</h2>
        </div>
        <p className="text-sm text-gray-500 font-semibold">
          Estado de salud de cada integración. Haz click en cualquier card para ir a la sección correspondiente.
        </p>
      </header>

      <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {overview.map((it) => (
          <button
            key={it.key}
            onClick={() => onJump?.(it.target)}
            className="surface p-5 text-left group relative transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300"
            style={{ borderTop: `3px solid ${it.accent}` }}
            data-testid={`resumen-card-${it.key}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="kpi-icon-clean" style={{ color: it.accent, boxShadow: `inset 0 0 0 1px ${it.accent}1f` }}>
                {it.icon}
              </div>
              <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider" style={{ color: STATUS_DOT[it.status] }}>
                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOT[it.status] }}/>
                {STATUS_LABEL[it.status]}
              </span>
            </div>
            <div className="text-xs font-extrabold uppercase tracking-wider text-gray-500">{it.title}</div>
            <div className="font-display font-extrabold mt-2" style={{ fontSize: 28, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {it.kpiValue}
            </div>
            <div className="text-xs font-bold text-gray-500 mt-2">{it.kpiLabel}</div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-[11px] font-bold text-gray-400">{it.last}</span>
              <span className="text-[11px] font-extrabold flex items-center gap-1 text-gray-500 group-hover:text-gray-900 transition-colors">
                Abrir <ArrowRight size={11} className="transition-transform group-hover:translate-x-0.5"/>
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
